import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Center,
  Group,
  Loader,
  Stack,
  Text,
} from '@mantine/core';
import { AlertCircle, ArrowLeft, WifiOff } from 'react-feather';
import { MatchingHeader } from './components/MatchingHeader';
import { useMatchingDraft } from './hooks/useMatchingDraft';
import {
  useMatchingSession,
  type ConnectionStatus,
} from './hooks/useMatchingSession';
import type { MatchingSessionState } from './model/types';
import { MatchingProgress } from './progress/MatchingProgress';
import { SetupFlow } from './SetupFlow';
import type { MatchingTransport } from './transport/types';

interface WorkspaceHeader {
  cohortId: string;
  cohortName: string;
  meta: string;
  statusLabel?: string;
  statusColor?: string;
}

interface MatchingWorkspaceProps extends WorkspaceHeader {
  transport: MatchingTransport;
  initialUrl?: string | null;
  mintUrl: () => Promise<string>;
  requestNumber?: number;
  onReviewResults?: () => void;
  reviewingResults?: boolean;
}

/**
 * Connects to a matching session and shows the setup flow or the progress
 * screen, depending on whether the run has started. Shared by the real page
 * and the /dev/matching harness, which differ only in transport and token.
 */
export function MatchingWorkspace({
  transport,
  initialUrl,
  mintUrl,
  requestNumber,
  onReviewResults,
  reviewingResults,
  ...header
}: MatchingWorkspaceProps) {
  const { session, connection, error, send, retry } = useMatchingSession({
    initialUrl,
    mintUrl,
    transport,
  });

  if (!session) {
    return (
      <Box maw={1360} mx="auto">
        <MatchingHeader {...header} />
        {connection === 'failed' ? (
          <Alert
            color="dangerRed"
            variant="light"
            icon={<AlertCircle size={18} />}
            title="Couldn't open this matching session"
          >
            <Stack gap="sm" align="flex-start">
              <Text size="sm">{error}</Text>
              <Button size="xs" variant="default" onClick={retry}>
                Try again
              </Button>
            </Stack>
          </Alert>
        ) : (
          <Center py={80}>
            <Stack align="center" gap="sm">
              <Loader />
              <Text c="dimmed">Connecting to the matching service…</Text>
            </Stack>
          </Center>
        )}
      </Box>
    );
  }

  return (
    <ConnectedWorkspace
      key={session.sessionId}
      session={session}
      send={send}
      connection={connection}
      error={error}
      onRetry={retry}
      requestNumber={requestNumber}
      onReviewResults={onReviewResults}
      reviewingResults={reviewingResults}
      header={header}
    />
  );
}

function ConnectedWorkspace({
  session,
  send,
  connection,
  error,
  onRetry,
  requestNumber,
  onReviewResults,
  reviewingResults,
  header,
}: {
  session: MatchingSessionState;
  send: (method: string, arg?: unknown) => boolean;
  connection: ConnectionStatus;
  error: string | null;
  onRetry: () => void;
  requestNumber?: number;
  onReviewResults?: () => void;
  reviewingResults?: boolean;
  header: WorkspaceHeader;
}) {
  const draft = useMatchingDraft({ session, send, connection, requestNumber });
  const [viewingSetup, setViewingSetup] = useState(false);
  const running = session.phase === 'running';

  return (
    <Box maw={1360} mx="auto">
      <MatchingHeader
        {...header}
        requestName={draft.config.request.name}
        saveState={running ? undefined : draft.saveState}
      />

      {connection === 'reconnecting' && (
        <Alert
          color="warningAmber"
          variant="light"
          icon={<WifiOff size={18} />}
          mb="lg"
        >
          Reconnecting to the matching service. Your changes will be saved when
          the connection is back.
        </Alert>
      )}
      {connection === 'failed' && (
        <Alert
          color="dangerRed"
          variant="light"
          icon={<AlertCircle size={18} />}
          title="Lost the connection to the matching service"
          mb="lg"
        >
          <Stack gap="sm" align="flex-start">
            <Text size="sm">
              {error ?? "We couldn't reconnect."} Changes you make now won't be
              saved until it reconnects.
            </Text>
            <Button size="xs" variant="default" onClick={onRetry}>
              Try again
            </Button>
          </Stack>
        </Alert>
      )}
      {draft.remoteChanged && (
        <Alert
          color="blue"
          variant="light"
          mb="lg"
          withCloseButton
          onClose={draft.dismissRemoteChanged}
          title="Updated by another admin"
        >
          Someone else changed this setup while you were editing. Your latest
          changes were kept.
        </Alert>
      )}

      {running && !viewingSetup && (
        <MatchingProgress
          session={session}
          cohortId={header.cohortId}
          onReviewResults={onReviewResults}
          reviewingResults={reviewingResults}
          onViewSetup={() => setViewingSetup(true)}
        />
      )}
      {running && viewingSetup && (
        <>
          <Group mb="md">
            <Button
              variant="default"
              leftSection={<ArrowLeft size={16} />}
              onClick={() => setViewingSetup(false)}
            >
              Back to progress
            </Button>
            <Text size="sm" c="dimmed">
              Matching has started, so this setup can't be changed.
            </Text>
          </Group>
          <SetupFlow session={session} draft={draft} send={send} readOnly />
        </>
      )}
      {!running && <SetupFlow session={session} draft={draft} send={send} />}
    </Box>
  );
}
